import type { CSSProperties } from 'react';
import {
  WiCloudy,
  WiDayCloudy,
  WiDaySunny,
  WiFog,
  WiNightAltCloudy,
  WiNightClear,
  WiRain,
  WiSleet,
  WiSnow,
  WiSprinkle,
  WiThunderstorm,
} from 'react-icons/wi';

import type { WmoIconKey } from './weatherDerive';

const ICON_MAP: Record<WmoIconKey, React.ComponentType<{ className?: string; style?: CSSProperties }>> = {
  'clear-day': WiDaySunny,
  'clear-night': WiNightClear,
  'partly-cloudy-day': WiDayCloudy,
  'partly-cloudy-night': WiNightAltCloudy,
  cloudy: WiCloudy,
  fog: WiFog,
  drizzle: WiSprinkle,
  rain: WiRain,
  snow: WiSnow,
  thunderstorm: WiThunderstorm,
  sleet: WiSleet,
};

/** Per-condition colour (see `--wx-*` tokens in index.css). Partly-cloudy blends toward neutral so full sun stands out. */
const ICON_TONE: Record<WmoIconKey, string> = {
  'clear-day': 'var(--wx-sun)',
  'partly-cloudy-day': 'color-mix(in oklab, var(--wx-sun) 55%, var(--text-secondary))',
  'clear-night': 'var(--wx-moon)',
  'partly-cloudy-night': 'color-mix(in oklab, var(--wx-moon) 45%, var(--text-secondary))',
  cloudy: 'var(--text-secondary)',
  fog: 'var(--text-tertiary)',
  drizzle: 'var(--wx-drizzle)',
  rain: 'var(--wx-rain)',
  snow: 'var(--wx-snow)',
  thunderstorm: 'var(--wx-storm)',
  sleet: 'var(--wx-sleet)',
};

interface WeatherIconProps {
  iconKey: WmoIconKey;
  /** Size in CSS units. Omit to size via `className` (e.g. responsive `text-*`). */
  size?: string | number;
  className?: string;
  /** Colour the icon by condition. Set false to inherit `currentColor`. */
  tone?: boolean;
  /** When provided the icon is treated as a meaningful image with this label, otherwise it is hidden from AT. */
  'aria-label'?: string;
}

export default function WeatherIcon({ iconKey, size, className, tone = true, 'aria-label': ariaLabel }: WeatherIconProps) {
  const Cmp = ICON_MAP[iconKey];
  return (
    <span
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      className={className}
      style={{ fontSize: size, display: 'inline-flex', lineHeight: 1, color: tone ? ICON_TONE[iconKey] : undefined }}
    >
      <Cmp />
    </span>
  );
}
